import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useStore } from "zustand";

import { Button } from "@react-spectrum/s2";
import PauseIcon from "icon:@app/assets/PauseFilled.svg";
import PlayIcon from "icon:@app/assets/PlayFilled.svg";

import { store } from "../store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { linearGradient } from "../views/projects/style" with { type: "macro" };

export const BackgroundVideo = ({ src, variant = "default" }) => {
  const videoRef = useRef(null);

  const colorScheme = useStore(store, (state) => state.colorScheme);

  const [isPlaying, setIsPlaying] = useState(
    localStorage.getItem("backgroundVideoPlaying") !== "false",
  );

  const handleTogglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }

    localStorage.setItem("backgroundVideoPlaying", String(!isPlaying));
  }, [isPlaying]);

  return (
    <div
      className={style({
        display: {
          default: "none",
          lg: "block",
        },
        position: "absolute",
        top: 0,
        left: 0,
        aspectRatio: "16 / 9",
        width: "full",
        // maxHeight: "full",
        overflow: "hidden",
        // zIndex: -1,
      })}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay={isPlaying}
        loop
        muted
        playsInline
        className={style({
          zIndex: -2,
          position: "absolute",
          top: 0,
          left: 0,
          size: "full",
          objectFit: "cover",
          objectPosition: "top",
          pointerEvents: "none",
        })}
      />
      <div
        className={style({
          zIndex: -1,
          position: "absolute",
          top: 0,
          left: 0,
          size: "full",
          backgroundImage: {
            variant: {
              default: linearGradient(
                "0deg",
                ["gray-25", 0],
                ["transparent", 100],
              ),
              faded: {
                colorScheme: {
                  dark: linearGradient(
                    "0deg",
                    ["gray-25", 0],
                    ["black/65", 100],
                  ),
                  light: linearGradient(
                    "0deg",
                    ["gray-25", 0],
                    ["white/25", 100],
                  ),
                },
              },
            },
          },
          pointerEvents: "none",
        })({ variant, colorScheme })}
      />
      <Button
        variant="secondary"
        staticColor="white"
        size="L"
        onPress={handleTogglePlay}
        aria-label={isPlaying ? "Pause video" : "Play video"}
        styles={style({
          position: "absolute",
          top: 16,
          insetEnd: 16,
          zIndex: 2,
        })}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </Button>
    </div>
  );
};

BackgroundVideo.propTypes = {
  src: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["default", "faded"]),
};
